import { javascript } from "@codemirror/lang-javascript";
import { EditorState, Prec } from "@codemirror/state";
import { EditorView, keymap } from "@codemirror/view";
import { basicSetup } from "codemirror";
import { useEffect, useRef } from "react";
import type { Theme } from "../../core/theme";
import { flashSpec, hoverSpec, lineHighlight } from "./lineHighlight";
import { setEditorTheme, tokyoNight } from "./theme";

interface CodeEditorProps {
  /** Initial document; later edits flow out through `onChange`. */
  initialValue: string;
  theme: Theme;
  label: string;
  readOnly?: boolean;
  onChange?: (value: string) => void;
  /** Bound to ⌘/Ctrl+Enter (ADR-0020). */
  onRun?: () => void;
  /** Scrolls to and flashes this 1-based line; a new object flashes again. */
  reveal?: { line: number } | null;
  /** Softly highlights this 1-based line (a hovered timeline row). */
  hoverLine?: number | null;
  testId?: string;
}

/** Thin React shell around a CodeMirror 6 view. */
export function CodeEditor({
  initialValue,
  theme,
  label,
  readOnly = false,
  onChange,
  onRun,
  reveal = null,
  hoverLine = null,
  testId = "code-editor",
}: CodeEditorProps) {
  const host = useRef<HTMLDivElement>(null);
  const view = useRef<EditorView | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onRunRef = useRef(onRun);
  onRunRef.current = onRun;
  const initialTheme = useRef(theme);

  // biome-ignore lint/correctness/useExhaustiveDependencies: the view is created once; theme updates go through setEditorTheme
  useEffect(() => {
    if (!host.current) return;
    const editor = new EditorView({
      parent: host.current,
      state: EditorState.create({
        doc: initialValue,
        extensions: [
          Prec.highest(
            keymap.of([
              {
                key: "Mod-Enter",
                run: () => {
                  onRunRef.current?.();
                  return true;
                },
              },
            ]),
          ),
          basicSetup,
          javascript(),
          tokyoNight(initialTheme.current),
          lineHighlight(),
          EditorState.readOnly.of(readOnly),
          EditorView.contentAttributes.of({ "aria-label": label }),
          EditorView.updateListener.of((update) => {
            if (update.docChanged) onChangeRef.current?.(update.state.doc.toString());
          }),
        ],
      }),
    });
    view.current = editor;
    return () => {
      editor.destroy();
      view.current = null;
    };
  }, []);

  useEffect(() => {
    if (view.current) setEditorTheme(view.current, theme);
  }, [theme]);

  useEffect(() => {
    const editor = view.current;
    if (editor && reveal) editor.dispatch(flashSpec(editor.state, reveal.line));
  }, [reveal]);

  useEffect(() => {
    const editor = view.current;
    if (editor) editor.dispatch(hoverSpec(editor.state, hoverLine));
  }, [hoverLine]);

  return <div ref={host} className="h-full overflow-hidden" data-testid={testId} />;
}
